import { ChangeEvent, useEffect } from "react";
import { Box, Grid, MenuItem } from "@mui/material";
import { useResetInputs } from "src/Contexts";
import { useAuthContext } from "src/Contexts/AuthContext/authContext";
import { useConfigPageContext } from "src/Contexts/configPagesContext/configPagesContext";
import { useContextReportsProduction } from "src/Contexts/ReportProduction/ReportsProductionContextContext";
import { InputsProductionContract } from "src/Contexts/ReportProduction/ReportsProductionTypes";
import { AppTextField } from "src/Pages/components/AppFormComponents/AppTextField";
import { SelectedFiliaisAutoCompletedGeneric } from "src/Pages/components/AutoCompleted/Multiple/SelectedFiliais";
import { ComponentPrivate } from "src/Pages/components/ComponentPrivate/ComponentPrivate";
import { DividerCore } from "src/Pages/components/DividerCore/DividerCore";
import { DrawerFilterReport } from "src/Pages/components/DrawerFilterReport/DrawerFilterReport";

import { AutoCompleteCorretorGeneric } from "../../../../../../components/AutoCompleted/AutoCompleteCorretor";
import { SelectedAdministradorasGeneric } from "../../../../../../components/AutoCompleted/Multiple/SelectedAdministradoras";
import { SelectedModalidadesGeneric } from "../../../../../../components/AutoCompleted/Multiple/SelectedModalidades";
import { SelectedOperadorasGeneric } from "../../../../../../components/AutoCompleted/Multiple/SelectedOperadoras";

import { UseReportProductionContract } from "./Form/Hooks/UseReportProductionContract";

export const DrawerFilterProductionContract = () => {
  const { setActive_incluir, active_incluir } = useConfigPageContext();
  const { handleSubmit } = UseReportProductionContract();
  const { resetInputs } = useResetInputs(true);
  const { userPerfil } = useAuthContext();

  const { setValuesInputsProductionContract, valuesInputsProductionContract } =
    useContextReportsProduction();

  useEffect(() => setActive_incluir(true), [active_incluir]);

  const handleChange =
    (prop: keyof InputsProductionContract) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      setValuesInputsProductionContract({
        ...valuesInputsProductionContract,
        [prop]: event.target.value,
      });
    };

  return (
    <DrawerFilterReport onClear={resetInputs} onApply={handleSubmit}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          "& .MuiAutocomplete-root": { margin: 0 },
        }}
      >
        <Box>
          <DividerCore title="Período" sxStyle={{ margin: "0 0 16px 0" }} />

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={4}>
              <AppTextField
                fullWidth
                label="Data inicial *"
                type="date"
                variant="outlined"
                sxStyle={{ margin: 0 }}
                value={valuesInputsProductionContract?.start_date}
                onChange={handleChange("start_date")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <AppTextField
                fullWidth
                label="Data final *"
                type="date"
                variant="outlined"
                sxStyle={{ margin: 0 }}
                value={valuesInputsProductionContract?.end_date}
                onChange={handleChange("end_date")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <AppTextField
                fullWidth
                select
                label="Tipo de data *"
                variant="outlined"
                sxStyle={{ margin: 0 }}
                value={valuesInputsProductionContract?.type_date}
                onChange={handleChange("type_date")}
              >
                <MenuItem value="ASSINATURA">Assinatura</MenuItem>
                <MenuItem value="VIGENCIA">Vigência</MenuItem>
                <MenuItem value="EMISSAO">Emissão</MenuItem>
                <MenuItem value="VENCIMENTO">Vencimento</MenuItem>
              </AppTextField>
            </Grid>
          </Grid>
        </Box>

        <Box>
          <DividerCore
            title="Operadoras e Planos"
            sxStyle={{ margin: "0 0 16px 0" }}
          />
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} sm={4}>
              <Box sx={{ "& .MuiAutocomplete-root": { mt: 0, mb: 0 } }}>
                <SelectedOperadorasGeneric
                  fullWidth
                  label="Operadoras"
                  variant="outlined"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ "& .MuiAutocomplete-root": { mt: 0, mb: 0 } }}>
                <SelectedModalidadesGeneric
                  fullWidth
                  label="Modalidades"
                  variant="outlined"
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Box sx={{ "& .MuiAutocomplete-root": { mt: 0, mb: 0 } }}>
                <SelectedAdministradorasGeneric
                  fullWidth
                  label="Administradoras"
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        <ComponentPrivate isView={userPerfil?.type_user !== "PRODUCER"}>
          <Box>
            <DividerCore
              title="Produtor e Equipe"
              sxStyle={{ margin: "0 0 16px 0" }}
            />
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={4}>
                <AppTextField
                  fullWidth
                  select
                  label="Produtor *"
                  variant="outlined"
                  sxStyle={{ margin: 0 }}
                  value={valuesInputsProductionContract?.type_report}
                  onChange={handleChange("type_report")}
                >
                  <MenuItem value="ALL">Todos</MenuItem>
                  <MenuItem value="UNIQUE">Por produtor</MenuItem>
                </AppTextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ "& .MuiAutocomplete-root": { mt: 0, mb: 0 } }}>
                  <AutoCompleteCorretorGeneric
                    fullWidth
                    disabled={
                      valuesInputsProductionContract.type_report === "ALL"
                    }
                    label="Corretor *"
                    variant="outlined"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ "& .MuiAutocomplete-root": { mt: 0, mb: 0 } }}>
                  <SelectedFiliaisAutoCompletedGeneric
                    fullWidth
                    label="Filiais"
                    variant="outlined"
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </ComponentPrivate>

        <ComponentPrivate isView={userPerfil?.type_user !== "PRODUCER"}>
          <Box>
            <DividerCore title="Opções" sxStyle={{ margin: "0 0 16px 0" }} />
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={6}>
                <AppTextField
                  fullWidth
                  select
                  label="Agrupar por corretor"
                  variant="outlined"
                  sxStyle={{ margin: 0 }}
                  value={valuesInputsProductionContract?.is_group}
                  onChange={handleChange("is_group")}
                >
                  <MenuItem value="TRUE">Sim</MenuItem>
                  <MenuItem value="FALSE">Não</MenuItem>
                </AppTextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <AppTextField
                  fullWidth
                  select
                  label="Fonte do lead"
                  variant="outlined"
                  sxStyle={{ margin: 0 }}
                  value={valuesInputsProductionContract?.font_lead}
                  onChange={handleChange("font_lead")}
                >
                  <MenuItem value="NONE">Não informar</MenuItem>
                  <MenuItem value="EMAIL">Email</MenuItem>
                  <MenuItem value="FACEBOOK">FaceBook</MenuItem>
                  <MenuItem value="FORMULARIOS">Formulários</MenuItem>
                  <MenuItem value="GOOGLE">Google</MenuItem>
                  <MenuItem value="INDICACAO">Indicação</MenuItem>
                  <MenuItem value="INSTAGRAM">Instagram</MenuItem>
                  <MenuItem value="LADING_PAGE">Lading Page</MenuItem>
                  <MenuItem value="LISTAGEM">Listagem</MenuItem>
                  <MenuItem value="MARKETING">Marketing</MenuItem>
                  <MenuItem value="PLANTAO">Plantão</MenuItem>
                  <MenuItem value="RAFAH">Rafah</MenuItem>
                  <MenuItem value="AMIL">Amil</MenuItem>
                  <MenuItem value="MED_SENIOR">MedSênior</MenuItem>
                  <MenuItem value="VERA_CRUZ">Vera Cruz</MenuItem>
                  <MenuItem value="SUL_AMERICA">SulAmérica</MenuItem>
                  <MenuItem value="UNI_ODONTO">Uniodonto</MenuItem>
                  <MenuItem value="CARTEIRA_CORRETOR">
                    Carteira Corretor
                  </MenuItem>
                  <MenuItem value="PROSPECCAO">Prospecção</MenuItem>
                </AppTextField>
              </Grid>
            </Grid>
          </Box>
        </ComponentPrivate>
      </Box>
    </DrawerFilterReport>
  );
};
